/**
 * ╔══════════════════════════════════════════════════════════════╗
 * ║          METEOR STORM  ·  AAA Masterpiece Edition           ║
 * ║                                                              ║
 * ║  Architecture fixes vs previous version:                    ║
 * ║  • Canvas is sized ONLY by ResizeObserver (never in RAF)    ║
 * ║  • position:fixed wrapper → always fills viewport exactly   ║
 * ║  • Single canvas (bg + game drawn together each frame)      ║
 * ║  • RAF reads canvas.width/height — never touches DOM layout ║
 * ║  • All mutable state lives in gsRef (zero stale closures)   ║
 * ║  • try/catch around every sfx call                          ║
 * ╚══════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { sfxCorrect, sfxWrong, sfxReward, sfxLevelUp } from "@/utils/soundEngine";

/* ─────────────────────────────────────────────────────────────────────────────
   QUESTION BANK
───────────────────────────────────────────────────────────────────────────── */
const QUESTIONS = [
  // Mathe – easy
  { q: "6 × 7 = ?",        correct: "42",         wrong: ["36","48","54"],                    cat: "Mathe",   diff: "easy"   },
  { q: "72 ÷ 9 = ?",       correct: "8",          wrong: ["7","9","6"],                       cat: "Mathe",   diff: "easy"   },
  { q: "15 + 28 = ?",      correct: "43",         wrong: ["41","45","42"],                    cat: "Mathe",   diff: "easy"   },
  { q: "64 - 37 = ?",      correct: "27",         wrong: ["25","29","31"],                    cat: "Mathe",   diff: "easy"   },
  { q: "4² = ?",           correct: "16",         wrong: ["8","12","18"],                     cat: "Mathe",   diff: "easy"   },
  { q: "3 × 13 = ?",       correct: "39",         wrong: ["36","42","33"],                    cat: "Mathe",   diff: "easy"   },
  { q: "56 ÷ 7 = ?",       correct: "8",          wrong: ["6","7","9"],                       cat: "Mathe",   diff: "easy"   },
  { q: "9 × 8 = ?",        correct: "72",         wrong: ["64","81","63"],                    cat: "Mathe",   diff: "easy"   },
  { q: "25 + 47 = ?",      correct: "72",         wrong: ["70","74","68"],                    cat: "Mathe",   diff: "easy"   },
  { q: "50 - 18 = ?",      correct: "32",         wrong: ["30","34","28"],                    cat: "Mathe",   diff: "easy"   },
  { q: "7 × 6 = ?",        correct: "42",         wrong: ["48","35","40"],                    cat: "Mathe",   diff: "easy"   },
  { q: "81 ÷ 9 = ?",       correct: "9",          wrong: ["8","7","11"],                      cat: "Mathe",   diff: "easy"   },
  // Mathe – normal
  { q: "√81 = ?",          correct: "9",          wrong: ["8","7","11"],                      cat: "Mathe",   diff: "normal" },
  { q: "100 - 63 = ?",     correct: "37",         wrong: ["33","38","47"],                    cat: "Mathe",   diff: "normal" },
  { q: "120 ÷ 8 = ?",      correct: "15",         wrong: ["12","18","14"],                    cat: "Mathe",   diff: "normal" },
  { q: "13 × 7 = ?",       correct: "91",         wrong: ["84","97","88"],                    cat: "Mathe",   diff: "normal" },
  { q: "144 ÷ 12 = ?",     correct: "12",         wrong: ["11","13","14"],                    cat: "Mathe",   diff: "normal" },
  { q: "17 + 48 = ?",      correct: "65",         wrong: ["63","67","55"],                    cat: "Mathe",   diff: "normal" },
  { q: "200 - 137 = ?",    correct: "63",         wrong: ["67","57","73"],                    cat: "Mathe",   diff: "normal" },
  { q: "5³ = ?",           correct: "125",        wrong: ["115","135","150"],                 cat: "Mathe",   diff: "normal" },
  // Mathe – hard
  { q: "17 × 13 = ?",      correct: "221",        wrong: ["211","231","217"],                 cat: "Mathe",   diff: "hard"   },
  { q: "√196 = ?",         correct: "14",         wrong: ["13","15","16"],                    cat: "Mathe",   diff: "hard"   },
  { q: "2⁸ = ?",           correct: "256",        wrong: ["248","264","512"],                 cat: "Mathe",   diff: "hard"   },
  { q: "360 ÷ 15 = ?",     correct: "24",         wrong: ["22","26","28"],                    cat: "Mathe",   diff: "hard"   },
  { q: "23 × 11 = ?",      correct: "253",        wrong: ["243","263","231"],                 cat: "Mathe",   diff: "hard"   },
  // Deutsch – easy
  { q: "Gegenteil von 'groß'?",      correct: "klein",      wrong: ["alt","lang","stark"],           cat: "Deutsch", diff: "easy"   },
  { q: "Mehrzahl von 'Kind'?",       correct: "Kinder",     wrong: ["Kinds","Kinde","Kindern"],      cat: "Deutsch", diff: "easy"   },
  { q: "Gegenteil von 'hell'?",      correct: "dunkel",     wrong: ["kalt","laut","weich"],          cat: "Deutsch", diff: "easy"   },
  { q: "Gegenteil von 'schnell'?",   correct: "langsam",    wrong: ["laut","groß","schwer"],         cat: "Deutsch", diff: "easy"   },
  { q: "Mehrzahl von 'Hund'?",       correct: "Hunde",      wrong: ["Hunds","Hunden","Hundes"],      cat: "Deutsch", diff: "easy"   },
  { q: "Gegenteil von 'alt'?",       correct: "jung",       wrong: ["neu","klein","leise"],          cat: "Deutsch", diff: "easy"   },
  // Deutsch – normal
  { q: "Synonym für 'schön'?",       correct: "hübsch",     wrong: ["groß","stark","schnell"],       cat: "Deutsch", diff: "normal" },
  { q: "Nomen von 'laufen'?",        correct: "Lauf",       wrong: ["Läufer","Laufend","Lauferei"],  cat: "Deutsch", diff: "normal" },
  { q: "Hauptstadt Österreichs?",    correct: "Wien",       wrong: ["Graz","Linz","Salzburg"],       cat: "Deutsch", diff: "normal" },
  { q: "Hauptstadt der Schweiz?",    correct: "Bern",       wrong: ["Zürich","Basel","Genf"],        cat: "Deutsch", diff: "normal" },
  { q: "Artikel von 'Sonne'?",       correct: "die",        wrong: ["der","das","einen"],            cat: "Deutsch", diff: "normal" },
  { q: "Artikel von 'Mond'?",        correct: "der",        wrong: ["die","das","ein"],              cat: "Deutsch", diff: "normal" },
  { q: "Vergangenheit von 'laufen'?",correct: "lief",       wrong: ["läufte","gelauft","laufte"],    cat: "Deutsch", diff: "normal" },
  // Deutsch – hard
  { q: "Genitiv von 'der Mann'?",    correct: "des Mannes", wrong: ["dem Mann","den Mann","des Mann"],cat:"Deutsch", diff: "hard"   },
  { q: "Konjunktiv II: 'ich haben'?",correct: "hätte",      wrong: ["habe","hatte","häbte"],         cat: "Deutsch", diff: "hard"   },
  { q: "Präteritum von 'sein'?",     correct: "war",        wrong: ["wäre","ist","sei"],             cat: "Deutsch", diff: "hard"   },
  // Natur – easy
  { q: "Planeten im Sonnensystem?",           correct: "8",          wrong: ["7","9","6"],                     cat: "Natur", diff: "easy"   },
  { q: "Beine einer Spinne?",                 correct: "8",          wrong: ["6","10","4"],                    cat: "Natur", diff: "easy"   },
  { q: "Größter Planet?",                     correct: "Jupiter",    wrong: ["Saturn","Uranus","Mars"],        cat: "Natur", diff: "easy"   },
  { q: "Wasser besteht aus?",                 correct: "H₂O",        wrong: ["CO₂","NaCl","O₂"],              cat: "Natur", diff: "easy"   },
  { q: "Beine eines Insekts?",                correct: "6",          wrong: ["4","8","10"],                    cat: "Natur", diff: "easy"   },
  { q: "Größtes Tier der Welt?",              correct: "Blauwal",    wrong: ["Elefant","Giraffe","Hai"],       cat: "Natur", diff: "easy"   },
  // Natur – normal
  { q: "Herzkammern des Menschen?",           correct: "4",          wrong: ["2","3","5"],                     cat: "Natur", diff: "normal" },
  { q: "Schnellstes Landtier?",               correct: "Gepard",     wrong: ["Adler","Puma","Pferd"],          cat: "Natur", diff: "normal" },
  { q: "Knochen im menschlichen Körper?",     correct: "206",        wrong: ["196","216","186"],               cat: "Natur", diff: "normal" },
  { q: "Eingeatmetes Gas?",                   correct: "Sauerstoff", wrong: ["Stickstoff","CO₂","Helium"],     cat: "Natur", diff: "normal" },
  { q: "Dauer einer Erdumdrehung?",           correct: "24 Std.",    wrong: ["12 Std.","48 Std.","36 Std."],   cat: "Natur", diff: "normal" },
  { q: "Lichtgeschwindigkeit (km/s)?",        correct: "300.000",    wrong: ["200.000","150.000","400.000"],   cat: "Natur", diff: "normal" },
  // Natur – hard
  { q: "Aggregatszustand Wasser bei 0°C?",    correct: "fest/flüssig",wrong:["fest","flüssig","gasförmig"],   cat: "Natur", diff: "hard"   },
  { q: "Ordnungszahl Kohlenstoff?",           correct: "6",          wrong: ["7","8","5"],                     cat: "Natur", diff: "hard"   },
  { q: "Chromosomenpaare des Menschen?",      correct: "23",         wrong: ["22","24","46"],                  cat: "Natur", diff: "hard"   },
  // Logik – easy
  { q: "Nächste Zahl: 2,4,6,8,?",    correct: "10", wrong: ["9","11","12"],  cat: "Logik", diff: "easy"   },
  { q: "Nächste Zahl: 5,10,15,20,?", correct: "25", wrong: ["22","30","24"], cat: "Logik", diff: "easy"   },
  { q: "Welches fehlt: ▲○▲○▲?",      correct: "○",  wrong: ["▲","■","●"],   cat: "Logik", diff: "easy"   },
  // Logik – normal
  { q: "Nächste Zahl: 2,4,8,16,?",   correct: "32", wrong: ["24","20","18"], cat: "Logik", diff: "normal" },
  { q: "Nächste Zahl: 1,1,2,3,5,?",  correct: "8",  wrong: ["7","9","6"],   cat: "Logik", diff: "normal" },
  { q: "5 + 3 × 2 = ?",              correct: "11", wrong: ["16","13","10"], cat: "Logik", diff: "normal" },
  { q: "(8 + 2) × 3 = ?",            correct: "30", wrong: ["14","24","26"], cat: "Logik", diff: "normal" },
  { q: "Nächste Zahl: 1,4,9,16,?",   correct: "25", wrong: ["20","24","22"], cat: "Logik", diff: "normal" },
  { q: "100 ÷ 4 × 3 = ?",            correct: "75", wrong: ["25","150","300"],cat: "Logik", diff: "normal" },
  // Logik – hard
  { q: "Nächste Zahl: 1,3,7,15,?",   correct: "31", wrong: ["25","29","33"], cat: "Logik", diff: "hard"   },
  { q: "2 + 2 × 2 - 2 ÷ 2 = ?",      correct: "5",  wrong: ["4","6","8"],   cat: "Logik", diff: "hard"   },
  { q: "Nächste Zahl: 1,8,27,64,?",  correct: "125",wrong: ["100","81","144"],cat:"Logik", diff: "hard"   },
];

/* ─────────────────────────────────────────────────────────────────────────────
   CONFIG
───────────────────────────────────────────────────────────────────────────── */
const HP_MAX    = 3;
const WAVE_SIZE = 5;   // correct hits per wave
const MAX_ON_SCREEN = 5;

const DIFF_CFG = {
  easy:   { label: "Leicht", speed: 80,  spawnMs: 2200, color: "#22c55e" },
  normal: { label: "Normal", speed: 130, spawnMs: 1600, color: "#06b6d4" },
  hard:   { label: "Schwer", speed: 190, spawnMs: 1100, color: "#ef4444" },
};

const PAL = {
  Mathe:   { bg: "#7c3aed", dark: "#4c1d95", text: "#c4b5fd", glow: "#8b5cf6", r: 139, g: 92,  b: 246 },
  Deutsch: { bg: "#0284c7", dark: "#0c4a6e", text: "#7dd3fc", glow: "#06b6d4", r: 6,   g: 182, b: 212 },
  Natur:   { bg: "#15803d", dark: "#052e16", text: "#86efac", glow: "#22c55e", r: 34,  g: 197, b: 94  },
  Logik:   { bg: "#b45309", dark: "#451a03", text: "#fcd34d", glow: "#fbbf24", r: 251, g: 191, b: 36  },
};
const PAL_DEFAULT = PAL.Mathe;
const CATS  = ["Alle", "Mathe", "Deutsch", "Natur", "Logik"];
const DIFFS = ["easy", "normal", "hard"];

/* ─────────────────────────────────────────────────────────────────────────────
   PURE HELPERS
───────────────────────────────────────────────────────────────────────────── */
function shuffle(a)    { return [...a].sort(() => Math.random() - 0.5); }
function rnd(a, b)     { return a + Math.random() * (b - a); }
function pick(a)       { return a[Math.floor(Math.random() * a.length)]; }

function buildQ(cat = "Alle", diff = "normal") {
  let pool = QUESTIONS;
  if (cat !== "Alle") pool = pool.filter(q => q.cat === cat);
  const dp = pool.filter(q => q.diff === diff);
  const base = dp.length ? dp : (pool.length ? pool : QUESTIONS);
  const q = pick(base);
  return { ...q, allAnswers: shuffle([q.correct, ...q.wrong.slice(0, 3)]) };
}

function makeRock(n = 10) {
  return Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2;
    const r = rnd(0.68, 1.0);
    return { x: Math.cos(a) * r, y: Math.sin(a) * r };
  });
}

function makeCraters(n) {
  return Array.from({ length: n }, () => ({ x: rnd(-0.5, 0.5), y: rnd(-0.5, 0.5), r: rnd(0.06, 0.14) }));
}

function makeStars(W, H) {
  const LAYERS = [
    { n: 80, speed: 18, rMin: 0.3, rMax: 0.8,  op: 0.35 },
    { n: 50, speed: 35, rMin: 0.6, rMax: 1.2,  op: 0.55 },
    { n: 20, speed: 60, rMin: 1.0, rMax: 2.0,  op: 0.80 },
  ];
  return LAYERS.map(l =>
    Array.from({ length: l.n }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: rnd(l.rMin, l.rMax), op: l.op * rnd(0.5, 1),
      vy: l.speed * rnd(0.7, 1.3),
      tw: rnd(0.5, 2.5), tOff: Math.random() * Math.PI * 2,
    }))
  );
}

function makeExplosion(cx, cy, pal, big, t0) {
  const n = big ? 24 : 14;
  return {
    cx, cy, t0, dur: big ? 900 : 600, maxR: big ? 90 : 55,
    pts: Array.from({ length: n }, () => ({
      a: Math.random() * Math.PI * 2,
      d: rnd(big ? 30 : 15, big ? 85 : 50),
      s: rnd(2, big ? 7 : 4.5),
      cr: `rgb(${pal.r},${pal.g},${pal.b})`,
    })),
  };
}

function comboLabel(combo) {
  if (combo >= 15) return { text: "LEGENDARY!", col: "#fbbf24" };
  if (combo >= 10) return { text: "UNSTOPPABLE!", col: "#f97316" };
  if (combo >= 5)  return { text: "ON FIRE!", col: "#ef4444" };
  if (combo >= 3)  return { text: `${combo}× COMBO!`, col: "#a78bfa" };
  return null;
}

function sfx(fn) { try { fn?.(); } catch {} }

/* ─────────────────────────────────────────────────────────────────────────────
   UI SUB-COMPONENTS
───────────────────────────────────────────────────────────────────────────── */
function HpBar({ hp }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {Array.from({ length: HP_MAX }, (_, i) => (
        <svg key={i} width="22" height="20" viewBox="0 0 24 22" fill="none">
          <path
            d="M12 20.5C12 20.5 2 13.5 2 7a5 5 0 0 1 10-1 5 5 0 0 1 10 1c0 6.5-10 13.5-10 13.5z"
            fill={i < hp ? "#ef4444" : "rgba(255,255,255,0.1)"}
            stroke={i < hp ? "#f87171" : "rgba(255,255,255,0.15)"}
            strokeWidth="1.5"
          />
        </svg>
      ))}
    </div>
  );
}

function PUBtn({ icon, label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={count === 0}
      style={{
        position: "relative", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 2,
        width: 54, height: 54, borderRadius: 12,
        border: active ? "2px solid #06b6d4" : "2px solid rgba(255,255,255,0.1)",
        background: count > 0 ? (active ? "rgba(6,182,212,0.18)" : "rgba(255,255,255,0.07)") : "rgba(255,255,255,0.03)",
        cursor: count > 0 ? "pointer" : "not-allowed",
        opacity: count > 0 ? 1 : 0.32,
        transition: "all 0.15s", backdropFilter: "blur(8px)",
      }}
    >
      <span style={{ fontSize: 21, lineHeight: 1 }}>{icon}</span>
      <span style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontWeight: 600, letterSpacing: 0.3 }}>{label}</span>
      {count > 0 && (
        <span style={{
          position: "absolute", top: -5, right: -5,
          background: "#8b5cf6", color: "#fff", borderRadius: 99,
          width: 16, height: 16, fontSize: 9, fontWeight: 800,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: "2px solid #04040d",
        }}>{count}</span>
      )}
    </button>
  );
}

function WaveFlash({ wave }) {
  return (
    <div style={{
      position: "absolute", inset: 0,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      background: "rgba(4,4,13,0.7)", backdropFilter: "blur(4px)",
      zIndex: 50, pointerEvents: "none",
      animation: "wff 1.8s ease forwards",
    }}>
      <div style={{ fontSize: 12, letterSpacing: 6, color: "#06b6d4", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>Welle</div>
      <div style={{ fontSize: 80, fontWeight: 900, color: "#fff", textShadow: "0 0 40px #8b5cf6, 0 0 80px #8b5cf6", lineHeight: 1 }}>{wave}</div>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: 10 }}>Tempo steigt!</div>
      <style>{`@keyframes wff{0%{opacity:0;transform:scale(.7)}20%{opacity:1;transform:scale(1.05)}60%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(1.1)}}`}</style>
    </div>
  );
}

function GameOver({ stats, onRestart, onMenu }) {
  const { score, maxCombo, correct, total, wave, xp, coins } = stats;
  const acc   = total > 0 ? Math.round((correct / total) * 100) : 0;
  const grade = acc >= 95 ? "S" : acc >= 80 ? "A" : acc >= 65 ? "B" : acc >= 50 ? "C" : "D";
  const gc    = { S: "#fbbf24", A: "#22c55e", B: "#06b6d4", C: "#a78bfa", D: "#ef4444" }[grade];

  return (
    <div style={{
      position: "absolute", inset: 0, background: "rgba(4,4,13,0.93)",
      backdropFilter: "blur(16px)", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      zIndex: 100, overflowY: "auto", padding: "24px 16px",
    }}>
      <div style={{ maxWidth: 400, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
        <div style={{
          fontSize: 96, fontWeight: 900, lineHeight: 1, color: gc,
          textShadow: `0 0 40px ${gc}, 0 0 80px ${gc}`,
          animation: "gr 0.6s cubic-bezier(.34,1.56,.64,1) both",
        }}>{grade}</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: -10 }}>{acc}% Trefferquote</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, width: "100%" }}>
          {[
            { l: "Punkte",       v: score.toLocaleString("de-DE") },
            { l: "Max Combo",    v: `${maxCombo}×` },
            { l: "Richtig",      v: `${correct}/${total}` },
            { l: "Welle",        v: wave },
          ].map(({ l, v }) => (
            <div key={l} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", fontWeight: 600, marginBottom: 4 }}>{l}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>{v}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, width: "100%" }}>
          {[
            { icon: "⭐", label: "XP",     value: `+${xp}`,    color: "#fbbf24" },
            { icon: "🪙", label: "Münzen", value: `+${coins}`, color: "#f97316" },
          ].map(({ icon, label, value, color }) => (
            <div key={label} style={{
              flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{ fontSize: 28 }}>{icon}</span>
              <div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, width: "100%" }}>
          <button onClick={onRestart} style={{
            flex: 1, padding: "14px 0", borderRadius: 12, border: "none",
            background: "linear-gradient(135deg,#7c3aed,#4c1d95)", color: "#fff",
            fontWeight: 800, fontSize: 15, cursor: "pointer",
            boxShadow: "0 4px 24px rgba(139,92,246,0.4)",
          }}>Nochmal</button>
          <button onClick={onMenu} style={{
            flex: 1, padding: "14px 0", borderRadius: 12,
            border: "2px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.05)", color: "#fff",
            fontWeight: 700, fontSize: 15, cursor: "pointer",
          }}>Menü</button>
        </div>
      </div>
      <style>{`@keyframes gr{from{opacity:0;transform:scale(.3) rotate(-15deg)}to{opacity:1;transform:scale(1) rotate(0)}}`}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────────────────── */
export default function MeteorStorm() {
  const navigate = useNavigate();

  /* ── React UI state (minimal — only what the DOM overlay needs) ── */
  const [screen,       setScreen]       = useState("menu");
  const [menuCat,      setMenuCat]      = useState("Alle");
  const [menuDiff,     setMenuDiff]     = useState("normal");
  const [hudHp,        setHudHp]        = useState(HP_MAX);
  const [hudScore,     setHudScore]     = useState(0);
  const [hudWave,      setHudWave]      = useState(1);
  const [hudCombo,     setHudCombo]     = useState(1);
  const [hudQ,         setHudQ]         = useState(null);  // { q, cat }
  const [powerups,     setPowerups]     = useState({ slowmo: 2, shield: 1, hint: 1, bomb: 1 });
  const [waveFlashNum, setWaveFlashNum] = useState(0);     // >0 means show flash
  const [gameOverData, setGameOverData] = useState(null);

  /* ── Stable refs to React setters (used by RAF loop, no stale closures) ── */
  const UI = useRef({});
  UI.current = { setHudHp, setHudScore, setHudWave, setHudCombo, setHudQ, setPowerups, setWaveFlashNum, setScreen, setGameOverData };

  /* ── Canvas + wrapper refs ── */
  const canvasRef  = useRef(null);
  const wrapperRef = useRef(null);

  /* ── All mutable game state in ONE ref ── */
  const gs = useRef(null);   // game state
  const raf = useRef(null);  // RAF id

  /* ═════════════════════════════════════════════════════════════════
     CANVAS SIZING — ONLY via ResizeObserver, NEVER inside RAF
  ═════════════════════════════════════════════════════════════════ */
  const resizeCanvas = () => {
    const canvas  = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const { width, height } = wrapper.getBoundingClientRect();
    const W = Math.round(width)  || window.innerWidth;
    const H = Math.round(height) || window.innerHeight;
    if (canvas.width !== W || canvas.height !== H) {
      canvas.width  = W;
      canvas.height = H;
      // Rebuild star field for new size
      if (gs.current) {
        gs.current.stars = makeStars(W, H);
      }
    }
  };

  /* Initial size on mount */
  useLayoutEffect(() => {
    resizeCanvas();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Resize observer on wrapper */
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(resizeCanvas);
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ═════════════════════════════════════════════════════════════════
     GAME LOOP — mounted once, drives everything
  ═════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let lastTs = 0;
    let tAcc   = 0; // global time accumulator for animations

    /* ── Spawn one meteor ── */
    function spawnMeteor() {
      const g = gs.current;
      if (!g || !g.running) return;
      const W = canvas.width;

      const isBoss = g.wave > 1 && g.wave % 3 === 0 && !g.bossOnScreen && g.correctHits > 0 && g.correctHits % 10 === 0;
      if (isBoss) g.bossOnScreen = true;

      const radius = isBoss ? 58 : 36;
      const usedAns = new Set(g.meteors.map(m => m.answer));
      const free    = g.currentQ.allAnswers.filter(a => !usedAns.has(a));
      if (free.length === 0) return;

      const answer    = pick(free);
      const waveMult  = 1 + (g.wave - 1) * 0.14;
      const cfg       = DIFF_CFG[g.diff];
      const vy        = cfg.speed * waveMult;
      const vx        = rnd(-18, 18);
      const margin    = radius + 10;
      const cx        = rnd(margin, Math.max(margin + 1, W - margin));
      const pal       = PAL[g.currentQ.cat] || PAL_DEFAULT;

      g.meteors.push({
        id:        g.nextId++,
        answer,
        isCorrect: answer === g.currentQ.correct,
        cat:       g.currentQ.cat,
        pal,
        isBoss,
        hp:        isBoss ? 2 : 1,
        cx, cy: -(radius + 10), vx, vy,
        rot:       rnd(0, Math.PI * 2),
        rock:      makeRock(),
        craters:   makeCraters(isBoss ? 5 : 3),
        hintOn:    false,
        radius,
        spawnT:    performance.now(),
      });
    }

    /* ── End game ── */
    function endGame() {
      const g = gs.current;
      if (!g || g.ended) return;
      g.ended   = true;
      g.running = false;

      const xp    = Math.floor(g.score / 50);
      const coins = Math.floor(g.score / 200) + (g.wave - 1) * 2;

      api.post("/students/me/minigame-reward", { coins, xp }).catch(() => {});
      sfx(sfxReward);

      UI.current.setGameOverData({
        score: g.score, maxCombo: g.maxCombo,
        correct: g.correctHits, total: g.correctHits + g.wrongHits,
        wave: g.wave, xp, coins,
      });
      UI.current.setScreen("over");
    }

    /* ── Hit a meteor ── */
    function hitMeteor(meteor, hx, hy, timestamp) {
      const g = gs.current;
      if (!g || !g.running) return;

      const pal = meteor.pal || PAL_DEFAULT;

      if (meteor.isCorrect) {
        // Boss needs 2 hits
        if (meteor.isBoss && meteor.hp > 1) {
          meteor.hp--;
          g.explosions.push(makeExplosion(hx, hy, pal, false, timestamp));
          sfx(sfxCorrect);
          return;
        }

        // Correct!
        g.combo       = Math.min(g.combo + 1, 20);
        g.maxCombo    = Math.max(g.maxCombo, g.combo);
        const pts     = 100 * Math.min(g.combo, 10) * (meteor.isBoss ? 3 : 1);
        g.score      += pts;
        g.correctHits++;
        g.waveHits++;

        g.meteors = g.meteors.filter(m => m.id !== meteor.id);
        if (meteor.isBoss) g.bossOnScreen = false;

        g.explosions.push(makeExplosion(hx, hy, pal, meteor.isBoss, timestamp));
        g.scorePopups.push({ x: hx, y: hy, text: `+${pts}`, color: pal.glow, t0: timestamp, big: meteor.isBoss });

        const cl = comboLabel(g.combo);
        if (cl) g.comboPopups.push({ x: hx, y: hy - 36, text: cl.text, color: cl.col, t0: timestamp });

        // Wave advance
        if (g.waveHits >= WAVE_SIZE) {
          g.waveHits = 0;
          g.wave++;
          UI.current.setHudWave(g.wave);
          UI.current.setWaveFlashNum(g.wave);
          setTimeout(() => UI.current.setWaveFlashNum(0), 1800);
          sfx(sfxLevelUp);
        }

        // Refresh question if no more open answers
        const taken = new Set(g.meteors.map(m => m.answer));
        const avail = g.currentQ.allAnswers.filter(a => !taken.has(a));
        if (avail.length === 0) {
          g.currentQ = buildQ(g.cat, g.diff);
          // Re-assign correct flags on remaining meteors
          g.meteors.forEach(m => { m.isCorrect = (m.answer === g.currentQ.correct); });
          UI.current.setHudQ({ q: g.currentQ.q, cat: g.currentQ.cat });
        }

        UI.current.setHudScore(g.score);
        UI.current.setHudCombo(g.combo);
        sfx(sfxCorrect);

      } else {
        // Wrong
        g.wrongHits++;
        g.combo     = 1;
        g.shakeEnd  = timestamp + 380;
        g.meteors   = g.meteors.filter(m => m.id !== meteor.id);
        g.explosions.push(makeExplosion(hx, hy, { r: 239, g: 68, b: 68 }, false, timestamp));

        if (g.shield) {
          g.shield = false;
          UI.current.setPowerups(p => ({ ...p }));
        } else {
          g.hp = Math.max(0, g.hp - 1);
          UI.current.setHudHp(g.hp);
          if (g.hp <= 0) { endGame(); return; }
        }
        UI.current.setHudCombo(1);
        sfx(sfxWrong);
      }
    }

    /* ── Physics update ── */
    function update(dt, timestamp) {
      const g = gs.current;
      if (!g || !g.running || g.ended) return;

      const W = canvas.width;
      const H = canvas.height;

      // Stars
      const starDt = dt;
      g.stars.forEach(layer =>
        layer.forEach(s => {
          s.y += s.vy * starDt;
          if (s.y > H + s.r) { s.y = -s.r; s.x = Math.random() * W; }
        })
      );

      // SlowMo factor
      const isSlow   = g.slowUntil > timestamp;
      const dtGame   = isSlow ? dt * 0.25 : dt;

      // Hint expiry
      if (g.hintUntil > 0 && timestamp > g.hintUntil) {
        g.hintUntil = 0;
        g.meteors.forEach(m => { m.hintOn = false; });
      }

      // Move meteors
      g.meteors.forEach(m => {
        m.cy  += m.vy * dtGame;
        m.cx  += m.vx * dtGame;
        m.rot += 0.35 * dtGame;
        // Bounce off side walls
        if (m.cx - m.radius < 0)     { m.cx = m.radius;     m.vx = Math.abs(m.vx); }
        if (m.cx + m.radius > W)     { m.cx = W - m.radius; m.vx = -Math.abs(m.vx); }
      });

      // Meteors escaped
      const escaped = g.meteors.filter(m => m.cy > H + 90);
      escaped.forEach(m => {
        if (m.isCorrect) {
          if (!g.shield) {
            g.hp = Math.max(0, g.hp - 1);
            UI.current.setHudHp(g.hp);
            g.shakeEnd = timestamp + 380;
            if (g.hp <= 0) { endGame(); return; }
          } else {
            g.shield = false;
          }
          // New question when correct meteor escapes
          g.currentQ = buildQ(g.cat, g.diff);
          g.meteors.forEach(m2 => { m2.isCorrect = (m2.answer === g.currentQ.correct); });
          UI.current.setHudQ({ q: g.currentQ.q, cat: g.currentQ.cat });
        }
        if (m.isBoss) g.bossOnScreen = false;
      });
      g.meteors = g.meteors.filter(m => m.cy <= H + 90);

      // Spawn
      const cfg      = DIFF_CFG[g.diff];
      const interval = cfg.spawnMs / (1 + (g.wave - 1) * 0.08);
      if (timestamp - g.lastSpawn > interval && g.meteors.length < MAX_ON_SCREEN) {
        spawnMeteor();
        g.lastSpawn = timestamp;
      }
    }

    /* ── Draw a single meteor ── */
    function drawMeteor(m, timestamp) {
      const { cx, cy, radius, pal, rot, rock, craters, isBoss, hintOn, hp } = m;
      if (!pal) return;
      const { r, g: gv, b } = pal;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);

      // Tail
      const tailLen = isBoss ? 120 : 70;
      for (let i = 0; i < 3; i++) {
        const sp = (i - 1) * radius * 0.22;
        const tg = ctx.createLinearGradient(sp, 0, sp * 0.5, -tailLen);
        tg.addColorStop(0, `rgba(${r},${gv},${b},0.5)`);
        tg.addColorStop(1, "transparent");
        ctx.beginPath();
        ctx.moveTo(sp, 0);
        ctx.lineTo(sp * 0.5, -tailLen);
        ctx.strokeStyle = tg;
        ctx.lineWidth   = isBoss ? 5 - i : 3 - i * 0.5;
        ctx.lineCap = "round";
        ctx.stroke();
      }

      // Glow
      const gr = ctx.createRadialGradient(0, 0, radius * 0.3, 0, 0, radius + 22);
      gr.addColorStop(0, `rgba(${r},${gv},${b},0.35)`);
      gr.addColorStop(1, "transparent");
      ctx.beginPath(); ctx.arc(0, 0, radius + 22, 0, Math.PI * 2);
      ctx.fillStyle = gr; ctx.fill();

      // Rock body
      const bg = ctx.createRadialGradient(-radius * 0.3, -radius * 0.3, 0, 0, 0, radius);
      bg.addColorStop(0, pal.bg);
      bg.addColorStop(1, pal.dark);
      ctx.beginPath();
      ctx.moveTo(rock[0].x * radius, rock[0].y * radius);
      for (let i = 1; i < rock.length; i++) ctx.lineTo(rock[i].x * radius, rock[i].y * radius);
      ctx.closePath();
      ctx.fillStyle = bg; ctx.fill();

      // Sheen
      const sh = ctx.createRadialGradient(-radius * 0.35, -radius * 0.35, 0, 0, 0, radius * 0.8);
      sh.addColorStop(0, "rgba(255,255,255,0.18)"); sh.addColorStop(1, "transparent");
      ctx.fillStyle = sh; ctx.fill();

      // Craters
      craters.forEach(cr => {
        ctx.beginPath(); ctx.arc(cr.x * radius, cr.y * radius, cr.r * radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.22)"; ctx.fill();
        ctx.beginPath(); ctx.arc(cr.x * radius - cr.r * radius * 0.2, cr.y * radius - cr.r * radius * 0.2, cr.r * radius * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.07)"; ctx.fill();
      });

      // Hint ring
      if (hintOn) {
        const pulse = 0.5 + 0.5 * Math.sin(timestamp * 0.008);
        ctx.beginPath(); ctx.arc(0, 0, radius + 11, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${r},${gv},${b},${pulse})`; ctx.lineWidth = 3; ctx.stroke();
      }

      // Boss rings
      if (isBoss) {
        const ba = 0.55 + 0.4 * Math.sin(timestamp * 0.005);
        ctx.beginPath(); ctx.arc(0, 0, radius + 14, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(251,191,36,${ba})`; ctx.lineWidth = 3;
        ctx.setLineDash([8, 4]); ctx.stroke(); ctx.setLineDash([]);
        if (hp >= 2) {
          ctx.beginPath(); ctx.arc(0, 0, radius + 23, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(251,191,36,0.22)"; ctx.lineWidth = 2; ctx.stroke();
        }
        ctx.font = "bold 17px sans-serif"; ctx.textAlign = "center";
        ctx.fillStyle = "#fbbf24"; ctx.fillText("👑", 0, -radius - 6);
      }

      ctx.restore();

      // Answer text (outside rotate so it stays upright)
      const fs = m.answer.length > 6 ? (isBoss ? 14 : 11) : (isBoss ? 18 : 14);
      ctx.font        = `900 ${fs}px system-ui, sans-serif`;
      ctx.textAlign   = "center";
      ctx.textBaseline = "middle";
      ctx.shadowColor = pal.glow; ctx.shadowBlur = 12;
      ctx.fillStyle   = pal.text;
      ctx.fillText(m.answer, cx, cy);
      ctx.shadowBlur  = 0;
    }

    /* ── Draw explosion ── */
    function drawExplosion(exp, timestamp) {
      const p = Math.min((timestamp - exp.t0) / exp.dur, 1);
      if (p >= 1) return;
      exp.pts.forEach(pt => {
        const px = exp.cx + Math.cos(pt.a) * pt.d * p;
        const py = exp.cy + Math.sin(pt.a) * pt.d * p;
        const alpha = 1 - p;
        const sz = pt.s * (1 - p * 0.5);
        ctx.beginPath(); ctx.arc(px, py, Math.max(0.1, sz), 0, Math.PI * 2);
        ctx.fillStyle   = pt.cr.replace("rgb(", "rgba(").replace(")", `,${alpha})`);
        ctx.shadowColor = pt.cr; ctx.shadowBlur = 8;
        ctx.fill(); ctx.shadowBlur = 0;
      });
      ctx.beginPath(); ctx.arc(exp.cx, exp.cy, p * exp.maxR, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255,255,255,${(1 - p) * 0.6})`;
      ctx.lineWidth   = 3 * (1 - p); ctx.stroke();
    }

    /* ── Draw floating text popup ── */
    function drawPopup(popup, timestamp, dur) {
      const p = Math.min((timestamp - popup.t0) / dur, 1);
      if (p >= 1) return;
      const alpha = 1 - p;
      const y     = popup.y - p * 55;
      ctx.font         = `900 ${popup.big ? 22 : 17}px system-ui, sans-serif`;
      ctx.textAlign    = "center";
      ctx.textBaseline = "middle";
      ctx.shadowColor  = popup.color; ctx.shadowBlur = 14;
      ctx.globalAlpha  = alpha;
      ctx.fillStyle    = popup.color;
      ctx.fillText(popup.text, popup.x, y);
      ctx.globalAlpha  = 1; ctx.shadowBlur = 0;
    }

    function drawComboPopup(popup, timestamp) {
      const dur = 1200;
      const p   = Math.min((timestamp - popup.t0) / dur, 1);
      if (p >= 1) return;
      const scale = p < 0.15 ? p / 0.15 : p > 0.75 ? 1 - (p - 0.75) / 0.25 : 1;
      const y = popup.y - p * 38;
      ctx.save();
      ctx.translate(popup.x, y);
      ctx.scale(scale * 1.2, scale * 1.2);
      ctx.font         = "900 21px system-ui, sans-serif";
      ctx.textAlign    = "center"; ctx.textBaseline = "middle";
      ctx.shadowColor  = popup.color; ctx.shadowBlur = 20;
      ctx.globalAlpha  = scale;
      ctx.fillStyle    = popup.color;
      ctx.fillText(popup.text, 0, 0);
      ctx.globalAlpha  = 1; ctx.shadowBlur = 0;
      ctx.restore();
    }

    /* ── Full render ── */
    function render(timestamp) {
      const W = canvas.width;
      const H = canvas.height;
      const g = gs.current;

      // ── Background
      const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
      bgGrad.addColorStop(0,   "#04040d");
      bgGrad.addColorStop(0.5, "#06061a");
      bgGrad.addColorStop(1,   "#04040d");
      ctx.fillStyle = bgGrad; ctx.fillRect(0, 0, W, H);

      // Nebula blobs
      const n1 = ctx.createRadialGradient(W * 0.2, H * 0.18, 0, W * 0.2, H * 0.18, Math.min(W, H) * 0.28);
      n1.addColorStop(0, "rgba(139,92,246,0.055)"); n1.addColorStop(1, "transparent");
      ctx.fillStyle = n1; ctx.fillRect(0, 0, W, H);
      const n2 = ctx.createRadialGradient(W * 0.82, H * 0.72, 0, W * 0.82, H * 0.72, Math.min(W, H) * 0.24);
      n2.addColorStop(0, "rgba(6,182,212,0.05)"); n2.addColorStop(1, "transparent");
      ctx.fillStyle = n2; ctx.fillRect(0, 0, W, H);

      // Stars
      if (g && g.stars) {
        g.stars.forEach(layer =>
          layer.forEach(s => {
            const twinkle = 0.7 + 0.3 * Math.sin(tAcc * s.tw + s.tOff);
            ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255,255,255,${s.op * twinkle})`; ctx.fill();
          })
        );
      }

      if (!g || !g.running) return;

      // Screen shake
      let sx = 0, sy = 0;
      if (g.shakeEnd > timestamp) {
        const intens = ((g.shakeEnd - timestamp) / 380) * 6;
        sx = (Math.random() - 0.5) * intens;
        sy = (Math.random() - 0.5) * intens;
      }
      ctx.save(); ctx.translate(sx, sy);

      // Shield overlay
      if (g.shield) {
        const pulse = 0.3 + 0.15 * Math.sin(tAcc * 0.004);
        const rad   = Math.max(W, H) * 0.52;
        const sg    = ctx.createRadialGradient(W / 2, H / 2, rad * 0.85, W / 2, H / 2, rad);
        sg.addColorStop(0, "transparent");
        sg.addColorStop(0.8, `rgba(6,182,212,${pulse * 0.28})`);
        sg.addColorStop(1,   `rgba(6,182,212,${pulse})`);
        ctx.beginPath(); ctx.arc(W / 2, H / 2, rad, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(6,182,212,${pulse * 0.7})`; ctx.lineWidth = 2; ctx.stroke();
        ctx.fillStyle = sg; ctx.fill();
      }

      // Meteors
      g.meteors.forEach(m => drawMeteor(m, timestamp));

      // Explosions (pruned)
      g.explosions = g.explosions.filter(e => timestamp - e.t0 < e.dur);
      g.explosions.forEach(e => drawExplosion(e, timestamp));

      // Popups (pruned)
      g.scorePopups = g.scorePopups.filter(p => timestamp - p.t0 < 900);
      g.scorePopups.forEach(p => drawPopup(p, timestamp, 900));
      g.comboPopups = g.comboPopups.filter(p => timestamp - p.t0 < 1200);
      g.comboPopups.forEach(p => drawComboPopup(p, timestamp));

      // SlowMo bar
      if (g.slowUntil > timestamp) {
        const rem  = (g.slowUntil - timestamp) / 5000;
        const barW = Math.min(W * 0.5, 240);
        const barX = W / 2 - barW / 2;
        const barY = H - 72;
        ctx.fillStyle = "rgba(250,204,21,0.1)";
        ctx.beginPath(); ctx.rect(barX, barY, barW, 4); ctx.fill();
        const bGrad = ctx.createLinearGradient(barX, 0, barX + barW * rem, 0);
        bGrad.addColorStop(0, "#fde047"); bGrad.addColorStop(1, "#f59e0b");
        ctx.fillStyle = bGrad;
        ctx.beginPath(); ctx.rect(barX, barY, barW * rem, 4); ctx.fill();
      }

      ctx.restore();
    }

    /* ── THE LOOP ── */
    function loop(timestamp) {
      const dt = lastTs === 0 ? 0 : Math.min((timestamp - lastTs) / 1000, 0.05);
      lastTs = timestamp;
      tAcc  += dt;

      update(dt, timestamp);
      render(timestamp);

      raf.current = requestAnimationFrame(loop);
    }

    raf.current = requestAnimationFrame(loop);

    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []); // mounted once — RAF owns its lifecycle

  /* ═════════════════════════════════════════════════════════════════
     TOUCH / CLICK HANDLER
  ═════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function getPos(e) {
      const rect = canvas.getBoundingClientRect();
      if (e.changedTouches && e.changedTouches.length > 0) {
        return { x: e.changedTouches[0].clientX - rect.left, y: e.changedTouches[0].clientY - rect.top };
      }
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    function onTap(e) {
      e.preventDefault();
      const g = gs.current;
      if (!g || !g.running || g.ended) return;
      const { x, y } = getPos(e);
      const timestamp = performance.now();
      const meteors = g.meteors;
      for (let i = meteors.length - 1; i >= 0; i--) {
        const m = meteors[i];
        const hitR = m.radius + 14; // fat-finger margin
        if (Math.hypot(x - m.cx, y - m.cy) < hitR) {
          // call the hitMeteor function — but it's defined inside the other useEffect!
          // we use the ref pattern instead:
          hitMeteorRef.current(m, x, y, timestamp);
          return;
        }
      }
    }

    canvas.addEventListener("touchend", onTap, { passive: false });
    canvas.addEventListener("click",    onTap);
    return () => {
      canvas.removeEventListener("touchend", onTap);
      canvas.removeEventListener("click",    onTap);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* We need hitMeteor accessible from the click handler — use a ref bridge */
  const hitMeteorRef = useRef(null);

  // Build hitMeteor as a stable closure over gsRef and UI ref
  useEffect(() => {
    hitMeteorRef.current = function hitMeteor(meteor, hx, hy, timestamp) {
      const g = gs.current;
      if (!g || !g.running || g.ended) return;
      const pal = meteor.pal || PAL_DEFAULT;

      if (meteor.isCorrect) {
        if (meteor.isBoss && meteor.hp > 1) {
          meteor.hp--;
          g.explosions.push(makeExplosion(hx, hy, pal, false, timestamp));
          sfx(sfxCorrect);
          return;
        }

        g.combo       = Math.min(g.combo + 1, 20);
        g.maxCombo    = Math.max(g.maxCombo, g.combo);
        const pts     = 100 * Math.min(g.combo, 10) * (meteor.isBoss ? 3 : 1);
        g.score      += pts;
        g.correctHits++;
        g.waveHits++;

        g.meteors = g.meteors.filter(m => m.id !== meteor.id);
        if (meteor.isBoss) g.bossOnScreen = false;

        g.explosions.push(makeExplosion(hx, hy, pal, meteor.isBoss, timestamp));
        g.scorePopups.push({ x: hx, y: hy, text: `+${pts}`, color: pal.glow, t0: timestamp, big: meteor.isBoss });

        const cl = comboLabel(g.combo);
        if (cl) g.comboPopups.push({ x: hx, y: hy - 36, text: cl.text, color: cl.col, t0: timestamp });

        // Wave advance
        if (g.waveHits >= WAVE_SIZE) {
          g.waveHits = 0;
          g.wave++;
          UI.current.setHudWave(g.wave);
          UI.current.setWaveFlashNum(g.wave);
          setTimeout(() => UI.current.setWaveFlashNum(0), 1800);
          sfx(sfxLevelUp);
        }

        // Refresh question if no free answers
        const taken = new Set(g.meteors.map(m => m.answer));
        const avail = g.currentQ.allAnswers.filter(a => !taken.has(a));
        if (avail.length === 0) {
          g.currentQ = buildQ(g.cat, g.diff);
          g.meteors.forEach(m => { m.isCorrect = (m.answer === g.currentQ.correct); });
          UI.current.setHudQ({ q: g.currentQ.q, cat: g.currentQ.cat });
        }

        UI.current.setHudScore(g.score);
        UI.current.setHudCombo(g.combo);
        sfx(sfxCorrect);

      } else {
        g.wrongHits++;
        g.combo    = 1;
        g.shakeEnd = timestamp + 380;
        g.meteors  = g.meteors.filter(m => m.id !== meteor.id);
        g.explosions.push(makeExplosion(hx, hy, { r: 239, g: 68, b: 68 }, false, timestamp));

        if (g.shield) {
          g.shield = false;
          UI.current.setPowerups(p => ({ ...p }));
        } else {
          g.hp = Math.max(0, g.hp - 1);
          UI.current.setHudHp(g.hp);
          if (g.hp <= 0) {
            // end game
            g.ended = true; g.running = false;
            const xp    = Math.floor(g.score / 50);
            const coins = Math.floor(g.score / 200) + (g.wave - 1) * 2;
            api.post("/students/me/minigame-reward", { coins, xp }).catch(() => {});
            sfx(sfxReward);
            UI.current.setGameOverData({
              score: g.score, maxCombo: g.maxCombo,
              correct: g.correctHits, total: g.correctHits + g.wrongHits,
              wave: g.wave, xp, coins,
            });
            UI.current.setScreen("over");
            return;
          }
        }
        UI.current.setHudCombo(1);
        sfx(sfxWrong);
      }
    };
  }); // updates every render so closures are always fresh

  /* ═════════════════════════════════════════════════════════════════
     POWER-UP HANDLER
  ═════════════════════════════════════════════════════════════════ */
  function usePowerup(type) {
    const g = gs.current;
    if (!g || !g.running || g.powerups[type] <= 0) return;
    g.powerups[type]--;
    const now = performance.now();

    switch (type) {
      case "slowmo":
        g.slowUntil = now + 5000;
        break;
      case "shield":
        g.shield = true;
        break;
      case "hint":
        g.hintUntil = now + 4000;
        g.meteors.forEach(m => { m.hintOn = m.isCorrect; });
        break;
      case "bomb":
        g.meteors.filter(m => !m.isCorrect).forEach(m => {
          const pal = m.pal || PAL_DEFAULT;
          g.explosions.push(makeExplosion(m.cx, m.cy, pal, m.isBoss, now));
        });
        g.meteors = g.meteors.filter(m => m.isCorrect);
        break;
    }
    setPowerups({ ...g.powerups });
  }

  /* ═════════════════════════════════════════════════════════════════
     START GAME
  ═════════════════════════════════════════════════════════════════ */
  function startGame(cat, diff) {
    // Ensure canvas is correctly sized before reading dimensions
    resizeCanvas();
    const canvas = canvasRef.current;
    const W = canvas ? (canvas.width  || window.innerWidth)  : window.innerWidth;
    const H = canvas ? (canvas.height || window.innerHeight) : window.innerHeight;

    const firstQ = buildQ(cat, diff);

    gs.current = {
      running:      true,
      ended:        false,
      cat, diff,
      hp:           HP_MAX,
      score:        0,
      wave:         1,
      combo:        1,
      maxCombo:     1,
      correctHits:  0,
      wrongHits:    0,
      waveHits:     0,
      meteors:      [],
      explosions:   [],
      scorePopups:  [],
      comboPopups:  [],
      nextId:       1,
      currentQ:     firstQ,
      lastSpawn:    -9999,
      bossOnScreen: false,
      shield:       false,
      slowUntil:    0,
      hintUntil:    0,
      shakeEnd:     0,
      powerups:     { slowmo: 2, shield: 1, hint: 1, bomb: 1 },
      stars:        makeStars(W, H),
    };

    setHudHp(HP_MAX);
    setHudScore(0);
    setHudWave(1);
    setHudCombo(1);
    setHudQ({ q: firstQ.q, cat: firstQ.cat });
    setPowerups({ slowmo: 2, shield: 1, hint: 1, bomb: 1 });
    setWaveFlashNum(0);
    setGameOverData(null);
    setScreen("playing");
  }

  /* ═════════════════════════════════════════════════════════════════
     RENDER
  ═════════════════════════════════════════════════════════════════ */
  const catColor = hudQ ? (PAL[hudQ.cat]?.glow ?? "#8b5cf6") : "#8b5cf6";

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "fixed", inset: 0,
        overflow: "hidden",
        background: "#04040d",
        userSelect: "none", WebkitUserSelect: "none",
        fontFamily: "system-ui, -apple-system, sans-serif",
        touchAction: "none",
      }}
    >
      {/* ── The one canvas ── */}
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
      />

      {/* ══════════════ MENU ══════════════ */}
      {screen === "menu" && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 10,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          overflowY: "auto", padding: "32px 16px 48px",
        }}>
          <div style={{ maxWidth: 440, width: "100%", display: "flex", flexDirection: "column", gap: 24 }}>

            <button
              onClick={() => navigate(-1)}
              style={{
                alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6,
                background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10, padding: "8px 14px", color: "rgba(255,255,255,0.7)",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
              Zurück
            </button>

            <div style={{ textAlign: "center" }}>
              <div style={{
                fontSize: "clamp(38px, 9vw, 58px)", fontWeight: 900, lineHeight: 1,
                background: "linear-gradient(135deg, #c4b5fd, #8b5cf6, #06b6d4)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                marginBottom: 6,
              }}>☄️ MeteorStorm</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.45)" }}>Wissen gegen Meteore. Antworte schnell!</div>
            </div>

            {/* Category */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1.5, marginBottom: 10, textTransform: "uppercase" }}>Kategorie</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {CATS.map(c => {
                  const active = menuCat === c;
                  const col    = PAL[c];
                  return (
                    <button key={c} onClick={() => setMenuCat(c)} style={{
                      padding: "8px 16px", borderRadius: 99, fontWeight: 700, fontSize: 13, cursor: "pointer",
                      border: active ? `2px solid ${col?.glow ?? "#8b5cf6"}` : "2px solid rgba(255,255,255,0.1)",
                      background: active ? `${col?.glow ?? "#8b5cf6"}22` : "rgba(255,255,255,0.04)",
                      color: active ? (col?.glow ?? "#c4b5fd") : "rgba(255,255,255,0.6)",
                      transition: "all 0.15s",
                    }}>{c}</button>
                  );
                })}
              </div>
            </div>

            {/* Difficulty */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1.5, marginBottom: 10, textTransform: "uppercase" }}>Schwierigkeit</div>
              <div style={{ display: "flex", gap: 8 }}>
                {DIFFS.map(d => {
                  const cfg    = DIFF_CFG[d];
                  const active = menuDiff === d;
                  return (
                    <button key={d} onClick={() => setMenuDiff(d)} style={{
                      flex: 1, padding: "10px 0", borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: "pointer",
                      border: active ? `2px solid ${cfg.color}` : "2px solid rgba(255,255,255,0.1)",
                      background: active ? `${cfg.color}22` : "rgba(255,255,255,0.04)",
                      color: active ? cfg.color : "rgba(255,255,255,0.6)",
                      transition: "all 0.15s",
                    }}>{cfg.label}</button>
                  );
                })}
              </div>
            </div>

            {/* How-to */}
            <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "16px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", marginBottom: 10, letterSpacing: 1, textTransform: "uppercase" }}>So geht's</div>
              {[
                "Tippe auf den Meteor mit der richtigen Antwort",
                "Falsche Treffer kosten Leben — Präzision zählt!",
                "Baue Combos auf für vielfache Punkte",
                "Alle 5 Treffer steigt die Welle — mehr Tempo!",
                "Boss-Meteore brauchen 2 Treffer",
              ].map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 10, marginBottom: 5 }}>
                  <span style={{ color: "#8b5cf6", fontWeight: 800, fontSize: 14 }}>•</span>
                  <span style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>{t}</span>
                </div>
              ))}
            </div>

            {/* Power-ups */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1.5, marginBottom: 10, textTransform: "uppercase" }}>Power-ups</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[
                  { icon: "⏱", label: "Slow-Mo", desc: "75% langsamer für 5 Sekunden" },
                  { icon: "🛡", label: "Schild",  desc: "Absorbiert den nächsten Fehler" },
                  { icon: "💡", label: "Hinweis", desc: "Richtige Antwort leuchtet auf" },
                  { icon: "💣", label: "Bombe",   desc: "Vernichtet alle falschen Meteore" },
                ].map(({ icon, label, desc }) => (
                  <div key={label} style={{
                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)",
                    borderRadius: 10, padding: "10px 12px", display: "flex", alignItems: "flex-start", gap: 10,
                  }}>
                    <span style={{ fontSize: 22, lineHeight: 1 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => startGame(menuCat, menuDiff)}
              style={{
                width: "100%", padding: "16px 0", borderRadius: 14, border: "none",
                background: "linear-gradient(135deg, #7c3aed, #4c1d95)",
                color: "#fff", fontWeight: 900, fontSize: 18, cursor: "pointer",
                boxShadow: "0 6px 32px rgba(139,92,246,0.45)", letterSpacing: 0.5,
              }}
            >Jetzt spielen ☄️</button>
          </div>
        </div>
      )}

      {/* ══════════════ HUD (playing) ══════════════ */}
      {screen === "playing" && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", pointerEvents: "none", zIndex: 10 }}>
          {/* Top bar */}
          <div style={{
            background: "linear-gradient(to bottom, rgba(4,4,13,0.88) 0%, transparent 100%)",
            padding: "14px 16px 32px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <HpBar hp={hudHp} />
              <div style={{
                fontSize: "clamp(20px, 5vw, 28px)", fontWeight: 900, color: "#fff",
                textShadow: "0 0 20px #8b5cf6, 0 0 40px #8b5cf6", letterSpacing: -0.5,
              }}>{hudScore.toLocaleString("de-DE")}</div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
                <div style={{
                  background: "rgba(139,92,246,0.2)", border: "1px solid rgba(139,92,246,0.3)",
                  borderRadius: 99, padding: "3px 10px", fontSize: 12, fontWeight: 700, color: "#c4b5fd",
                }}>Welle {hudWave}</div>
                {hudCombo > 1 && (
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#fbbf24", textShadow: "0 0 10px #fbbf24" }}>
                    {hudCombo}× COMBO
                  </div>
                )}
              </div>
            </div>
            {hudQ && (
              <div style={{
                background: "rgba(255,255,255,0.05)", border: `1px solid ${catColor}44`,
                borderRadius: 12, padding: "10px 14px", boxShadow: `0 0 20px ${catColor}22`,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: catColor, marginBottom: 4, textTransform: "uppercase" }}>
                  {hudQ.cat}
                </div>
                <div style={{ fontSize: "clamp(13px, 3.5vw, 16px)", fontWeight: 700, color: "#fff", lineHeight: 1.35 }}>
                  {hudQ.q}
                </div>
              </div>
            )}
          </div>

          <div style={{ flex: 1 }} />

          {/* Power-up bar */}
          <div style={{
            background: "linear-gradient(to top, rgba(4,4,13,0.88) 0%, transparent 100%)",
            padding: "28px 16px 20px",
            display: "flex", justifyContent: "center", gap: 12,
            pointerEvents: "auto",
          }}>
            <PUBtn icon="⏱" label="Slow-Mo" count={powerups.slowmo} onClick={() => usePowerup("slowmo")} />
            <PUBtn icon="🛡" label="Schild"  count={powerups.shield} active={!!gs.current?.shield} onClick={() => usePowerup("shield")} />
            <PUBtn icon="💡" label="Hinweis" count={powerups.hint}   onClick={() => usePowerup("hint")} />
            <PUBtn icon="💣" label="Bombe"   count={powerups.bomb}   onClick={() => usePowerup("bomb")} />
          </div>
        </div>
      )}

      {/* Wave flash */}
      {screen === "playing" && waveFlashNum > 0 && (
        <div style={{ position: "absolute", inset: 0, zIndex: 20, pointerEvents: "none" }}>
          <WaveFlash wave={waveFlashNum} />
        </div>
      )}

      {/* ══════════════ GAME OVER ══════════════ */}
      {screen === "over" && gameOverData && (
        <GameOver
          stats={gameOverData}
          onRestart={() => startGame(menuCat, menuDiff)}
          onMenu={() => setScreen("menu")}
        />
      )}
    </div>
  );
}
